import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchPorAsins } from "../porAsinList/porAsinListSlice";
import { RootState } from "../app/rootReducer";
import {
  Table,
  CollectionPreferences,
  Header,
  SpaceBetween,
} from "@amzn/awsui-components-react/polaris";
import { fetchEditableAttributes } from "../porAsinList/porAsinListSlice";
import { SortFacet } from "../../server/asin-central/search";
import {
  ONE_CLICK_LAUNCH_COLUMN_DEFINITIONS,
  oneClickLaunchTableColumnDefinitions,
} from "./oneClickLaunchTable-config";
import {
  setAsinMarketplaces,
  setSelectedAsinMarketplaces,
  setPreferences,
  mergeVisibleContentPreferences,
  setSort,
  clearRemovedAsinMarketplaces,
  getSelectedItemKey,
} from "./oneClickLaunchTableConnector";
import {
  defaultSort,
  EmptyTableState,
} from "../porAsinList/TableCommonElements";
import { writeToLocalStorage } from "../../util/localStorageUtils";
import {
  Button,
  ButtonDropdown,
  Popover,
  StatusIndicator,
} from "@amzn/awsui-components-react";
import { AuthorizedAttribute } from "../../server/brass/model";
import { clearAllOclValues } from "./oneClickLaunchConnector";
import { trackButtonClick, trackComponentView } from "../telemetry/telemetry";
import OneClickLaunchModal from "./oneClickLaunchModal";
import {
  showOclPublishLaunchAttributesRemoveWeblabModal,
  showOclPublishLaunchDatesModal,
  showOclRemoveModal,
} from "./oneClickLaunchModalConnector";
import { LaunchSummaryComponent } from "./SummaryView";

const OneClickLaunchActionButtons = () => {
  return (
    <>
      <OneClickLaunchActionButton />
    </>
  );
};

const OneClickLaunchActionButton = () => {
  const dispatch = useDispatch();
  const { editableAttributes } = useSelector(
    (state: RootState) => state.porAsinList
  );

  const {
    asinMarketplaces,
    asinMarketplacesContainsOutOfBoundsDates,
    asinMarketplacesContainsUnselectedItems,
    asinMarketplacesContainsRestrictedItems,
    asinMarketplacesContainsSelectedItems,
  } = useSelector((state: RootState) => state.asinOneClickLaunchTableControl);

  function userIsAuthorized(editableAttributes: AuthorizedAttribute[]) {
    return editableAttributes.some(
      (attribute) =>
        attribute.attributeName === "one_click_launch" && attribute.authorized
    );
  }

  function getPopoverContent(): string[] {
    if (!userIsAuthorized(editableAttributes)) {
      return [
        "User is not authorized to perform Launch Actions. Please review Wiki under Permissions for additional details. contact PSLM for permissions",
      ];
    }
    if (asinMarketplaces.length < 1) {
      return [
        "Please expand your search/filter criteria on ASINs Tab to include at least one item",
      ];
    }
    const messages: string[] = [];
    if (asinMarketplacesContainsRestrictedItems) {
      messages.push("Please remove all restricted ASINs");
    }
    if (asinMarketplacesContainsUnselectedItems) {
      messages.push("Please select all items on the page");
    }
    if (asinMarketplacesContainsOutOfBoundsDates) {
      messages.push(
        "Please refine your search to include only items with an Announce Date within 24 hours"
      );
    }
    return messages;
  }

  function actionButtonShouldBeDisabled() {
    return (
      asinMarketplaces.length < 1 ||
      !userIsAuthorized(editableAttributes) ||
      asinMarketplacesContainsRestrictedItems ||
      asinMarketplacesContainsUnselectedItems ||
      asinMarketplacesContainsOutOfBoundsDates
    );
  }

  const actions: Record<string, () => void> = {
    "publish-launch-dates": () => {
      dispatch(showOclPublishLaunchDatesModal());
    },
    "publish-launch-attributes-remove-weblab": () => {
      dispatch(showOclPublishLaunchAttributesRemoveWeblabModal());
    },
  };

  return (
    <div data-testid="one-click-launch-action-buttons">
      <SpaceBetween direction="horizontal" size="xs">
        <Button
          data-testid="remove-from-launch-scope-button"
          variant="normal"
          disabled={!asinMarketplacesContainsSelectedItems}
          children="Remove from Launch Scope"
          onClick={() => {
            trackButtonClick(
              "oneClickLaunchTable",
              "RemoveFromLaunchScope",
              {}
            );
            dispatch(showOclRemoveModal());
          }}
        />
        {actionButtonShouldBeDisabled() && (
          <Popover
            dismissButton={true}
            header=""
            position="top"
            size="medium"
            triggerType="custom"
            content={getPopoverContent().map((line) => (
              <span>
                - {line}
                <br />
              </span>
            ))}
          >
            <StatusIndicator type="info" />
          </Popover>
        )}
        <ButtonDropdown
          data-testid="launch-actions-dropdown"
          variant="primary"
          children="Launch Actions"
          disabled={actionButtonShouldBeDisabled()}
          items={[
            {
              text: "Publish Launch Dates",
              id: "publish-launch-dates",
            },
            {
              text: "Publish Launch Attributes and Remove Weblab",
              id: "publish-launch-attributes-remove-weblab",
            },
          ]}
          onItemClick={(event) => {
            const key = event?.detail?.id;
            if (key !== null) {
              actions[key]?.();
            }
          }}
        />
      </SpaceBetween>
    </div>
  );
};

export const OneClickLaunchTable = () => {
  const dispatch = useDispatch();
  const { currentPagePorAsins, isLoading, isGettingEditableAttributes } =
    useSelector((state: RootState) => state.porAsinList);
  const {
    preferences,
    sort,
    descending,
    asinMarketplaces,
    selectedAsinMarketplaces,
    removedAsinMarketplacesKeys,
  } = useSelector((state: RootState) => state.asinOneClickLaunchTableControl);
  const {
    queryString,
    activePropertyFilters,
    activePropertyFiltersVisibilityEnabled,
  } = useSelector((state: RootState) => state.generateQuery);

  const getSort = (): SortFacet[] =>
    sort === undefined
      ? defaultSort
      : [
          {
            attributeName: sort?.sortingField,
            ascending: !descending,
          },
        ];

  useEffect(() => {
    if (queryString) {
      dispatch(fetchPorAsins(0, 500, queryString, getSort()));
    }
  }, [queryString, dispatch, sort, descending]);

  useEffect(() => {
    if (activePropertyFiltersVisibilityEnabled) {
      dispatch(
        mergeVisibleContentPreferences({
          activeColumns: Object.keys(activePropertyFilters ?? {}),
        })
      );
    }
  }, [queryString, activePropertyFiltersVisibilityEnabled]);

  useEffect(() => {
    trackComponentView("oneClickLaunchTable", "table");
    dispatch(clearRemovedAsinMarketplaces());
    dispatch(clearAllOclValues());
  }, []);

  useEffect(() => {
    if (!isGettingEditableAttributes) {
      dispatch(fetchEditableAttributes());
    }
  });

  useEffect(() => {
    // Remove asins that have already been removed.
    const previouslyRemovedAsinMarketplaces = new Set(
      removedAsinMarketplacesKeys
    );
    const asinMarketplaces = currentPagePorAsins.filter(
      (item) =>
        item.isRestricted ||
        !previouslyRemovedAsinMarketplaces.has(getSelectedItemKey(item))
    );
    dispatch(setAsinMarketplaces(asinMarketplaces));
    dispatch(setSelectedAsinMarketplaces(asinMarketplaces));
  }, [currentPagePorAsins]);

  return (
    <SpaceBetween size="l" data-testid="one-click-launch-table-wrapper">
      <LaunchSummaryComponent
        asinMarketplaces={asinMarketplaces}
        title={"Launch Scope Summary"}
        headerSize={"h2"}
      />
      <Table
        data-testid="one-click-launch-table"
        header={
          <Header
            data-testid="one-click-launch-header"
            variant="h1"
            actions={<OneClickLaunchActionButtons />}
            counter={`(${asinMarketplaces.length})`}
          >
            {`One Click Launch`}
          </Header>
        }
        onSelectionChange={({ detail }) => {
          dispatch(setSelectedAsinMarketplaces(detail.selectedItems));
        }}
        selectedItems={selectedAsinMarketplaces}
        selectionType="multi"
        loading={isLoading}
        loadingText={"Loading"}
        empty={EmptyTableState()}
        columnDefinitions={ONE_CLICK_LAUNCH_COLUMN_DEFINITIONS}
        items={asinMarketplaces}
        stickyHeader={true}
        sortingColumn={sort}
        sortingDescending={descending}
        onSortingChange={(event) => dispatch(setSort(event.detail))}
        visibleColumns={preferences.visibleContent}
        preferences={
          <CollectionPreferences
            data-testid="one-click-launch-preferences"
            onConfirm={(event) => {
              writeToLocalStorage(
                "ONE_CLICK_LAUNCH_TABLE_PREFERENCES",
                JSON.stringify(event.detail)
              );
              dispatch(setPreferences(event.detail));
            }}
            title={"Preferences"}
            confirmLabel={"Confirm"}
            cancelLabel={"Cancel"}
            disabled={isLoading}
            preferences={preferences}
            visibleContentPreference={oneClickLaunchTableColumnDefinitions}
          />
        }
        className={"porTable"}
      ></Table>
      <OneClickLaunchModal />
    </SpaceBetween>
  );
};
